import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import ExamTypeSection from './ExamTypeSection';
import MaternalStructureTabs, { createMaternalStructures } from './MaternalStructureTabs';
import {
  generateReportFiles, saveReportFiles, describeSavedReport,
  reservePreviewWindow, abandonPreviewWindow, showReportPreview, postReportAudit
} from '../generators/reportOutput';
import { useTechnologists } from '../hooks/useTechnologists';

// New structure for multiple fetuses
const INITIAL_BPP_DATA = {
  examType: 'BPP',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: 'Fetal well being',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  examTypeDetails: {
    transabdominal: true,
    transvaginal: false,
    transvaginalOnly: false
  },
  numberOfFetuses: 1,  // ← Changed to number, not string
  chorionicity: '',  // ← For multiple pregnancies
  
  // SHARED sections (not per-fetus)
  maternalStructures: createMaternalStructures(),
  
  // NEW: Array of fetuses (fetal environment moved per-fetus)
  fetuses: [
    {
      id: 1,
      placentaLocation: 'Anterior',
      placentaGrade: '0',
      placentaPrevia: 'None',
      umbilicalCord: '3 vessel cord',
      amnioticFluid: 'Normal',
      afi: '',
      presentation: 'Cephalic',
      maternalLocation: 'Maternal Right',
      fetalHeartRate: '',
      gender: 'Not determined',
      anatomy: {
        bladder: 'Seen - Normal',
        kidneys: 'Seen - Normal',
        fourCH: 'Seen - Normal',
        stomach: 'Seen - Normal'
      },
      biophysicalProfile: {
        breathing: '2',
        movement: '2',
        tone: '2',
        fluid: '2'
      },
      gestationalAge: {
        clinicalEGA: { weeks: '', days: '' },
        clinicalEDD: '',
        lmp: ''
      },
      uaDoppler: {
        fetal: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
        midCord: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
        placenta: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' }
      },
      showUADoppler: false  // ← Per-fetus toggle
    }
  ]
};

function BPPForm({ token, onBack, facilityInfo }) {
  // Use useState instead of useUltrasoundForm for multi-fetus structure 
  const [formData, setFormData] = useState(INITIAL_BPP_DATA);
  const [activeFetusTab, setActiveFetusTab] = useState(0);  // ← NEW: Track active tab
  const [activeMaternalTab, setActiveMaternalTab] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReferenceRange, setShowReferenceRange] = useState(false);
  const [focusedDiastolicField, setFocusedDiastolicField] = useState(null);
  const [focusedBPPField, setFocusedBPPField] = useState(null);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();
  useEffect(() => {
  if (defaultTech && !formData.technologist) {
    setFormData(prev => ({ ...prev, technologist: defaultTech }));
  }
}, [defaultTech, formData.technologist]);

  const currentFetus = formData.fetuses[activeFetusTab];

  const formatDate = useCallback((value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  }, []);

  const updateCurrentFetus = (updates) => {
  setFormData(prev => ({
    ...prev,
    fetuses: prev.fetuses.map((fetus, index) =>
      index === activeFetusTab ? { ...fetus, ...updates } : fetus
    )
  }));
};

const updateCurrentFetusNested = (section, updates) => {
  setFormData(prev => ({
    ...prev,
    fetuses: prev.fetuses.map((fetus, index) =>
      index === activeFetusTab
        ? { ...fetus, [section]: { ...fetus[section], ...updates } }
        : fetus
    )
  }));
};

const handleNumberOfFetusesChange = (num) => {
  const newNum = parseInt(num);
  
  // Template for new fetus
  const createFetus = (id) => ({
    id,
    placentaLocation: 'Anterior',
    placentaGrade: '0',
    placentaPrevia: 'None',
    umbilicalCord: '3 vessel cord',
    amnioticFluid: 'Normal',
    afi: '',
    presentation: 'Cephalic',
    maternalLocation: id === 1 ? 'Maternal Right' : 'Maternal Left',
    fetalHeartRate: '',
    gender: 'Not determined',
    anatomy: {
      bladder: 'Seen - Normal',
      kidneys: 'Seen - Normal',
      fourCH: 'Seen - Normal',
      stomach: 'Seen - Normal'
    },
    biophysicalProfile: {
      breathing: '2',
      movement: '2',
      tone: '2',
      fluid: '2'
    },
    gestationalAge: {
      clinicalEGA: { weeks: '', days: '' },
      clinicalEDD: '',
      lmp: ''
    },
    uaDoppler: {
      fetal: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
      midCord: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
      placenta: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' }
    },
    showUADoppler: false
  });

    setFormData(prev => {
    const currentFetuses = [...prev.fetuses];
    if (newNum > currentFetuses.length) {
      // Add new fetuses
      for (let i = currentFetuses.length; i < newNum; i++) {
        currentFetuses.push(createFetus(i + 1));
      }
    } else if (newNum < currentFetuses.length) {
      // Remove extra fetuses
      currentFetuses.splice(newNum);
    }
    return { ...prev, numberOfFetuses: newNum, fetuses: currentFetuses };
  });

  // Reset active tab if needed
  if (activeFetusTab >= newNum) {
    setActiveFetusTab(newNum - 1);
  }
};


const handleUADopplerChange = (location, field, value) => {
  updateCurrentFetusNested('uaDoppler', {
    ...currentFetus.uaDoppler,
    [location]: {
      ...currentFetus.uaDoppler[location],
      [field]: value
    }
  });
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setMessage({ type: '', text: '' });

  try {
    const files = await generateReportFiles('BPP', formData, facilityInfo);
    await saveReportFiles(files);
    setMessage({ type: 'success', text: describeSavedReport(files) });
    postReportAudit({ apiUrl: process.env.REACT_APP_API_URL, token, examType: 'BPP', facilityInfo });
  } catch (error) {
    setMessage({ 
      type: 'error', 
      text: `✗ Error: ${error.message}` 
    });
    console.error('Error:', error);
  } finally {
    setIsSubmitting(false);
  }
};

const handlePreview = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setMessage({ type: '', text: '' });
  const previewWindow = reservePreviewWindow(facilityInfo);
  try {
    const files = await generateReportFiles('BPP', formData, facilityInfo);
    await showReportPreview(files, previewWindow);
  } catch (error) {
    abandonPreviewWindow(previewWindow);
    setMessage({ type: 'error', text: `✗ Error: ${error.message}` });
    console.error('Preview error:', error);
  } finally {
    setIsSubmitting(false);
  }
};

const handleMaternalChange = (changes) => {
  setFormData(prev => ({
    ...prev,
    maternalStructures: { ...prev.maternalStructures, ...changes }
  }));
};


  // Get background color for BPP dropdown based on value and focus state
  const getBPPDropdownStyle = (fieldName, value) => {
    // If this dropdown is focused (opened), return white background
    if (focusedBPPField === fieldName) {
      return { backgroundColor: 'var(--color-input-bg)' };
    }
    
    // Otherwise, return color-coded background based on value
    if (value === '2') {
      return { backgroundColor: 'var(--color-status-ok-bg)' }; // Green (2 points - normal)
    } else if (value === '0') {
      return { backgroundColor: 'var(--color-status-bad-bg)' }; // Red (0 points - abnormal)
    }
    return { backgroundColor: 'var(--color-input-bg)' }; // Default/empty
  };

  const calculateBPPScore = useCallback(() => {
    const { breathing, movement, tone, fluid } = currentFetus.biophysicalProfile;
    const total = parseInt(breathing) + parseInt(movement) + parseInt(tone) + parseInt(fluid);
    return total;
  }, [currentFetus.biophysicalProfile]);
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };



  return (
    <div>
      <button onClick={onBack} className="back-btn">
        ← Back to Selection
      </button>

      <div className="form-container">
        <h2 style={{ textAlign: 'center', color: 'var(--color-primary)', marginBottom: '30px' }}>
          Biophysical Profile (BPP) Ultrasound Worksheet
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Patient Information */}
          <PatientInfo formData={formData} updateField={updateField} technologists={technologists} />

          {/* Exam Type */}
          <ExamTypeSection
            value={formData.examTypeDetails}
            onChange={(examTypeDetails) => updateField('examTypeDetails', examTypeDetails)}
          />

          {/* Maternal Structures */}
          <MaternalStructureTabs
            maternal={formData.maternalStructures}
            onChange={handleMaternalChange}
            activeTab={activeMaternalTab}
            onTabChange={setActiveMaternalTab}
          />

          {/* Gestational Age & Number of Fetuses */}
<div className="form-section">
  <h3>Gestational Age & Number of Fetuses</h3>
  
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
    {/* Left Column: Gestational Age */}
    <div>
      <h4 style={{ marginBottom: '15px', color: 'var(--color-primary)' }}>Gestational Age</h4>
      
      <div className="form-group">
        <label>Clinical Dates:</label>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={currentFetus.gestationalAge.clinicalEGA.weeks}
            onChange={(e) => {
              const numbers = e.target.value.replace(/\D/g, '');
              updateCurrentFetusNested('gestationalAge', {
                clinicalEGA: { ...currentFetus.gestationalAge.clinicalEGA, weeks: numbers }
              });
            }}
            placeholder="Weeks"
            style={{ width: '80px' }}
          />
          <span>weeks</span>
          <input
            type="text"
            value={currentFetus.gestationalAge.clinicalEGA.days}
            onChange={(e) => {
              const numbers = e.target.value.replace(/\D/g, '');
              updateCurrentFetusNested('gestationalAge', {
                clinicalEGA: { ...currentFetus.gestationalAge.clinicalEGA, days: numbers }
              });
            }}
            placeholder="Days"
            style={{ width: '80px' }}
          />
          <span>days</span>
        </div>
      </div>

      <div className="form-group" style={{ marginTop: '10px' }}>
        <label>EDD:</label>
        <input
          type="text"
          value={currentFetus.gestationalAge.clinicalEDD}
          onChange={(e) => {
            const formatted = formatDate(e.target.value);
            updateCurrentFetusNested('gestationalAge', {
              clinicalEDD: formatted
            });
          }}
          placeholder="MM/DD/YYYY"
          style={{ width: '150px' }}
        />
      </div>

      <div className="form-group" style={{ marginTop: '10px' }}>
        <label>LMP:</label>
        <input
          type="text"
          value={currentFetus.gestationalAge.lmp}
          onChange={(e) => {
            const formatted = formatDate(e.target.value);
            updateCurrentFetusNested('gestationalAge', { lmp: formatted });
          }}
          placeholder="Type: 01152025 = 01/15/2025"
          style={{ width: '200px' }}
        />
      </div>
    </div>

    {/* Right Column: Number of Fetuses & Chorionicity */}
    <div>
      <h4 style={{ marginBottom: '15px', color: 'var(--color-primary)' }}>Pregnancy Type</h4>
      
      <div className="form-group">
        <label>Number of Fetuses:</label>
        <select
          value={formData.numberOfFetuses}
          onChange={(e) => handleNumberOfFetusesChange(e.target.value)}
          style={{ width: '200px', fontSize: '16px', padding: '8px' }}
        >
          <option value="1">Single (1)</option>
          <option value="2">Twins (2)</option>
          <option value="3">Triplets (3)</option>
        </select>
      </div>

      <div className="form-group" style={{ marginTop: '10px' }}>
        <label>Chorionicity:</label>
        <select
          value={formData.chorionicity}
          onChange={(e) => setFormData(prev => ({ ...prev, chorionicity: e.target.value }))}
          disabled={formData.numberOfFetuses === 1}
          style={{
            width: '100%',
            maxWidth: '300px',
            fontSize: '16px',
            padding: '8px',
            backgroundColor: formData.numberOfFetuses === 1 ? 'var(--color-disabled-bg)' : 'var(--color-input-bg)',
            cursor: formData.numberOfFetuses === 1 ? 'not-allowed' : 'pointer',
            color: formData.numberOfFetuses === 1 ? 'var(--color-disabled-text)' : 'var(--color-text)'
          }}
        >
          <option value="">Select...</option>
          <option value="Dichorionic-Diamniotic">Dichorionic-Diamniotic</option>
          <option value="Monochorionic-Diamniotic">Monochorionic-Diamniotic</option>
          <option value="Monochorionic-Monoamniotic">Monochorionic-Monoamniotic</option>
          <option value="Indeterminate">Indeterminate</option>
        </select>
      </div>
    </div>
  </div>
</div>

{/* TAB NAVIGATION */}
<div className="form-section">
  <div style={{
    display: 'flex',
    borderBottom: '2px solid var(--color-primary)',
    marginBottom: '20px'
  }}>
    {formData.fetuses.map((fetus, index) => (
      <button
        key={fetus.id}
        type="button"
        onClick={() => setActiveFetusTab(index)}
        style={{
          padding: '12px 24px',
          border: 'none',
          backgroundColor: activeFetusTab === index ? 'var(--color-primary)' : 'var(--color-surface)',
          color: activeFetusTab === index ? 'var(--color-card)' : 'var(--color-text)',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: activeFetusTab === index ? 'bold' : 'normal',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          marginRight: '4px',
          transition: 'all 0.3s ease'
        }}
      >
        Fetus {fetus.id}
      </button>
    ))}
  </div>

  {/* Per-Fetus Content */}
  <div style={{
    padding: '20px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: '8px',
    minHeight: '400px'
  }}>
    <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>
      Fetus {currentFetus.id} Information
    </h3>
    
    {/* Fetal Environment - Per Fetus */}
    <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
      <h4>Fetal Environment</h4>
      
      <div className="form-row">
        <div className="form-group">
          <label>Placenta Location:</label>
          <select
            value={currentFetus.placentaLocation}
            onChange={(e) => updateCurrentFetus({ placentaLocation: e.target.value })}
          >
            <option value="Anterior">Anterior</option>
            <option value="Posterior">Posterior</option>
            <option value="Fundal">Fundal</option>
            <option value="Right lateral">Right lateral</option>
            <option value="Left lateral">Left lateral</option>
            <option value="Low-lying">Low-lying</option>
          </select>
        </div>

        <div className="form-group">
          <label>Placenta Grade:</label>
          <select
            value={currentFetus.placentaGrade}
            onChange={(e) => updateCurrentFetus({ placentaGrade: e.target.value })}
          >
            <option value="0">0</option>
            <option value="I">I</option>
            <option value="II">II</option>
            <option value="III">III</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Placenta Previa:</label>
          <select
            value={currentFetus.placentaPrevia}
            onChange={(e) => updateCurrentFetus({ placentaPrevia: e.target.value })}
          >
            <option value="None">None</option>
            <option value="Low-lying placenta">Low-lying placenta</option>
            <option value="Marginal previa">Marginal previa</option>
            <option value="Complete previa">Complete previa</option>
          </select>
        </div>

        <div className="form-group">
          <label>Umbilical Cord:</label>
          <select
            value={currentFetus.umbilicalCord}
            onChange={(e) => updateCurrentFetus({ umbilicalCord: e.target.value })}
          >
            <option value="3 vessel cord">3 vessel cord</option>
            <option value="2 vessel cord">2 vessel cord</option>
            <option value="Limited view">Limited view</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Amniotic Fluid:</label>
          <select
            value={currentFetus.amnioticFluid}
            onChange={(e) => updateCurrentFetus({ amnioticFluid: e.target.value })}
          >
            <option value="Normal">Normal</option>
            <option value="Oligohydramnios">Oligohydramnios</option>
            <option value="Polyhydramnios">Polyhydramnios</option>
          </select>
        </div>

        <div className="form-group">
          <label>AFI (cm):</label>
          <input
            type="text"
            value={currentFetus.afi}
            onChange={(e) => updateCurrentFetus({ afi: e.target.value })}
            placeholder="Amniotic fluid index"
          />
        </div>
      </div>
    </div>

    {/* Fetal Information - Updated */}
    <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
      <h4>Fetal Information</h4>
      
      <div className="form-row">
        <div className="form-group">
          <label>Presentation:</label>
          <select
            value={currentFetus.presentation}
            onChange={(e) => updateCurrentFetus({ presentation: e.target.value })}
          >
            <option value="Cephalic">Cephalic</option>
            <option value="Breech">Breech</option>
            <option value="Transverse">Transverse</option>
            <option value="Oblique">Oblique</option>
          </select>
        </div>

        <div className="form-group">
          <label>Maternal Location:</label>
          <select
            value={currentFetus.maternalLocation}
            onChange={(e) => updateCurrentFetus({ maternalLocation: e.target.value })}
            disabled={formData.numberOfFetuses === 1}
            style={{
              backgroundColor: formData.numberOfFetuses === 1 ? 'var(--color-disabled-bg)' : 'var(--color-input-bg)',
              cursor: formData.numberOfFetuses === 1 ? 'not-allowed' : 'pointer',
              color: formData.numberOfFetuses === 1 ? 'var(--color-disabled-text)' : 'var(--color-text)'
            }}
          >
            <option value="Maternal Right">Maternal Right</option>
            <option value="Maternal Left">Maternal Left</option>
            <option value="Indeterminate">Indeterminate</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Fetal Heart Rate (BPM):</label>
          <input
            type="text"
            value={currentFetus.fetalHeartRate}
            onChange={(e) => updateCurrentFetus({ fetalHeartRate: e.target.value })}
            placeholder="Enter heart rate in BPM"
          />
        </div>

        <div className="form-group">
          <label>Fetal Gender:</label>
          <select
            value={currentFetus.gender}
            onChange={(e) => updateCurrentFetus({ gender: e.target.value })}
          >
            <option value="Not determined">Not determined</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
      </div>
    </div>



          {/* Fetal Anatomy */}
          <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
  <h4>Fetal Anatomy</h4>
  
  <div className="form-row">
    <div className="form-group">
      <label>Bladder:</label>
      <select
        value={currentFetus.anatomy.bladder}
        onChange={(e) => updateCurrentFetusNested('anatomy', { bladder: e.target.value })}
      >
        <option value="Seen - Normal">Seen - Normal</option>
        <option value="Not Well Visualized">Not Well Visualized</option>
        <option value="Abnormal">Abnormal</option>
      </select>
    </div>

    <div className="form-group">
      <label>Kidneys:</label>
      <select
        value={currentFetus.anatomy.kidneys}
        onChange={(e) => updateCurrentFetusNested('anatomy', { kidneys: e.target.value })}
      >
        <option value="Seen - Normal">Seen - Normal</option>
        <option value="Not Well Visualized">Not Well Visualized</option>
        <option value="Abnormal">Abnormal</option>
      </select>
    </div>
  </div>

  <div className="form-row">
    <div className="form-group">
      <label>4CH:</label>
      <select
        value={currentFetus.anatomy.fourCH}
        onChange={(e) => updateCurrentFetusNested('anatomy', { fourCH: e.target.value })}
      >
        <option value="Seen - Normal">Seen - Normal</option>
        <option value="Not Well Visualized">Not Well Visualized</option>
        <option value="Abnormal">Abnormal</option>
      </select>
    </div>

    <div className="form-group">
      <label>Stomach:</label>
      <select
        value={currentFetus.anatomy.stomach}
        onChange={(e) => updateCurrentFetusNested('anatomy', { stomach: e.target.value })}
      >
        <option value="Seen - Normal">Seen - Normal</option>
        <option value="Not Well Visualized">Not Well Visualized</option>
        <option value="Abnormal">Abnormal</option>
      </select>
    </div>
  </div>
</div>

          {/* Biophysical Profile */}
          <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
  <h4>Biophysical Profile</h4>
  
  <div className="form-row">
    <div className="form-group">
      <label>Breathing:</label>
      <select
        value={currentFetus.biophysicalProfile.breathing}
        onChange={(e) => {
          updateCurrentFetusNested('biophysicalProfile', { breathing: e.target.value });
          setFocusedBPPField(null);
        }}
        onFocus={() => setFocusedBPPField('breathing')}
        onBlur={() => setFocusedBPPField(null)}
        style={getBPPDropdownStyle('breathing', currentFetus.biophysicalProfile.breathing)}
      >
        <option value="0">0</option>
        <option value="2">2</option>
      </select>
    </div>

    <div className="form-group">
      <label>Movement:</label>
      <select
        value={currentFetus.biophysicalProfile.movement}
        onChange={(e) => {
          updateCurrentFetusNested('biophysicalProfile', { movement: e.target.value });
          setFocusedBPPField(null);
        }}
        onFocus={() => setFocusedBPPField('movement')}
        onBlur={() => setFocusedBPPField(null)}
        style={getBPPDropdownStyle('movement', currentFetus.biophysicalProfile.movement)}
      >
        <option value="0">0</option>
        <option value="2">2</option>
      </select>
    </div>
  </div>

  <div className="form-row">
    <div className="form-group">
      <label>Tone:</label>
      <select
        value={currentFetus.biophysicalProfile.tone}
        onChange={(e) => {
          updateCurrentFetusNested('biophysicalProfile', { tone: e.target.value });
          setFocusedBPPField(null);
        }}
        onFocus={() => setFocusedBPPField('tone')}
        onBlur={() => setFocusedBPPField(null)}
        style={getBPPDropdownStyle('tone', currentFetus.biophysicalProfile.tone)}
      >
        <option value="0">0</option>
        <option value="2">2</option>
      </select>
    </div>

    <div className="form-group">
      <label>Fluid:</label>
      <select
        value={currentFetus.biophysicalProfile.fluid}
        onChange={(e) => {
          updateCurrentFetusNested('biophysicalProfile', { fluid: e.target.value });
          setFocusedBPPField(null);
        }}
        onFocus={() => setFocusedBPPField('fluid')}
        onBlur={() => setFocusedBPPField(null)}
        style={getBPPDropdownStyle('fluid', currentFetus.biophysicalProfile.fluid)}
      >
        <option value="0">0</option>
        <option value="2">2</option>
      </select>
    </div>
  </div>

  <div className="form-group">
    <label>Total Score:</label>
    <input
      type="text"
      value={`${calculateBPPScore()} / 8`}
      readOnly
      style={{ backgroundColor: 'var(--color-surface)', cursor: 'default', fontWeight: 'bold' }}
    />
  </div>
</div>

          {/* BPP & UA Doppler Toggle Buttons */}
          <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
  <h4 style={{ color: 'var(--color-primary)', marginBottom: '15px' }}>UA DOPPLER</h4>
  <button
    type="button"
    onClick={() => {
      if (currentFetus.showUADoppler) {
        // When removing UA Doppler, reset all values to defaults
        updateCurrentFetusNested('uaDoppler', {
          fetal: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
          midCord: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
          placenta: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' }
        });
      }
      updateCurrentFetus({ showUADoppler: !currentFetus.showUADoppler });
    }}
    style={{
      padding: '10px 20px',
      backgroundColor: 'var(--color-success-solid)',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold'
    }}
  >
    {currentFetus.showUADoppler ? '- Remove UA Doppler' : '+ Add Cord Doppler'}
  </button>
</div>

          {/* UA Doppler - Conditional */}
          {currentFetus.showUADoppler && (
          <div className="form-section">
            <h3>UA Doppler</h3>
            
            {/* Fetal */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Fetal:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>PSV (cm/sec):</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.fetal.psv}
                    onChange={(e) => handleUADopplerChange('fetal', 'psv', e.target.value)}
                    placeholder="PSV"
                  />
                </div>

                <div className="form-group">
                  <label>S:D Ratio:</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.fetal.sdRatio}
                    onChange={(e) => handleUADopplerChange('fetal', 'sdRatio', e.target.value)}
                    placeholder="S:D"
                  />
                </div>

                <div className="form-group">
                  <label>Percentile:</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.fetal.percentile}
                    onChange={(e) => handleUADopplerChange('fetal', 'percentile', e.target.value)}
                    placeholder="%ile"
                  />
                </div>

                <div className="form-group">
                  <label>Diastolic Flow:</label>
                  <select
                    value={currentFetus.uaDoppler.fetal.diastolicFlow}
                    onChange={(e) => {
                      handleUADopplerChange('fetal', 'diastolicFlow', e.target.value);
                      setFocusedDiastolicField(null);
                    }}
                    onFocus={() => setFocusedDiastolicField('fetal')}
                    onBlur={() => setFocusedDiastolicField(null)}
                    style={{
                      backgroundColor: focusedDiastolicField === 'fetal' ? 'var(--color-input-bg)' : 
                        currentFetus.uaDoppler.fetal.diastolicFlow === 'Present' ? 'var(--color-status-ok-bg)' : 
                        (currentFetus.uaDoppler.fetal.diastolicFlow === 'Absent' || currentFetus.uaDoppler.fetal.diastolicFlow === 'Reversed') ? 'var(--color-status-bad-bg)' : 'var(--color-input-bg)'
                    }}
                  >
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                    <option value="Reversed">Reversed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Mid Cord */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Mid Cord:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>PSV (cm/sec):</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.midCord.psv}
                    onChange={(e) => handleUADopplerChange('midCord', 'psv', e.target.value)}
                    placeholder="PSV"
                  />
                </div>

                <div className="form-group">
                  <label>S:D Ratio:</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.midCord.sdRatio}
                    onChange={(e) => handleUADopplerChange('midCord', 'sdRatio', e.target.value)}
                    placeholder="S:D"
                  />
                </div>

                <div className="form-group">
                  <label>Percentile:</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.midCord.percentile}
                    onChange={(e) => handleUADopplerChange('midCord', 'percentile', e.target.value)}
                    placeholder="%ile"
                  />
                </div>

                <div className="form-group">
                  <label>Diastolic Flow:</label>
                  <select
                    value={currentFetus.uaDoppler.midCord.diastolicFlow}
                    onChange={(e) => {
                      handleUADopplerChange('midCord', 'diastolicFlow', e.target.value);
                      setFocusedDiastolicField(null);
                    }}
                    onFocus={() => setFocusedDiastolicField('midCord')}
                    onBlur={() => setFocusedDiastolicField(null)}
                    style={{
                      backgroundColor: focusedDiastolicField === 'midCord' ? 'var(--color-input-bg)' : 
                        currentFetus.uaDoppler.midCord.diastolicFlow === 'Present' ? 'var(--color-status-ok-bg)' : 
                        (currentFetus.uaDoppler.midCord.diastolicFlow === 'Absent' || currentFetus.uaDoppler.midCord.diastolicFlow === 'Reversed') ? 'var(--color-status-bad-bg)' : 'var(--color-input-bg)'
                    }}
                  >
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                    <option value="Reversed">Reversed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Placenta */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Placenta:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>PSV (cm/sec):</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.placenta.psv}
                    onChange={(e) => handleUADopplerChange('placenta', 'psv', e.target.value)}
                    placeholder="PSV"
                  />
                </div>

                <div className="form-group">
                  <label>S:D Ratio:</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.placenta.sdRatio}
                    onChange={(e) => handleUADopplerChange('placenta', 'sdRatio', e.target.value)}
                    placeholder="S:D"
                  />
                </div>

                <div className="form-group">
                  <label>Percentile:</label>
                  <input
                    type="text"
                    value={currentFetus.uaDoppler.placenta.percentile}
                    onChange={(e) => handleUADopplerChange('placenta', 'percentile', e.target.value)}
                    placeholder="%ile"
                  />
                </div>

                <div className="form-group">
                  <label>Diastolic Flow:</label>
                  <select
                    value={currentFetus.uaDoppler.placenta.diastolicFlow}
                    onChange={(e) => {
                      handleUADopplerChange('placenta', 'diastolicFlow', e.target.value);
                      setFocusedDiastolicField(null);
                    }}
                    onFocus={() => setFocusedDiastolicField('placenta')}
                    onBlur={() => setFocusedDiastolicField(null)}
                    style={{
                      backgroundColor: focusedDiastolicField === 'placenta' ? 'var(--color-input-bg)' : 
                        currentFetus.uaDoppler.placenta.diastolicFlow === 'Present' ? 'var(--color-status-ok-bg)' : 
                        (currentFetus.uaDoppler.placenta.diastolicFlow === 'Absent' || currentFetus.uaDoppler.placenta.diastolicFlow === 'Reversed') ? 'var(--color-status-bad-bg)' : 'var(--color-input-bg)'
                    }}
                  >
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                    <option value="Reversed">Reversed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Reference Range Button */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowReferenceRange(!showReferenceRange)}
                className="add-mass-btn"
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  backgroundColor: 'var(--color-success-solid)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {showReferenceRange ? 'Hide Reference Range' : 'Show Reference Range'}
              </button>
            </div>

            {/* Tooltip with Image */}
            {showReferenceRange && (
              <div style={{
                position: 'relative',
                marginTop: '20px',
                padding: '10px',
                backgroundColor: 'var(--color-card)',
                border: '2px solid var(--color-primary)',
                borderRadius: '5px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowReferenceRange(false)}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    background: 'var(--color-danger-solid)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
                <img 
                  src="/images/UA_SD_indices.png" 
                  alt="UA S/D Indices Reference" 
                  style={{ width: '100%', maxWidth: '800px', height: 'auto', display: 'block', margin: '0 auto' }}
                />
              </div>
            )}
          </div>
          )}
           </div>
</div>

          {/* Comments */}
          <div className="form-section">
            <h3>Additional Comments</h3>
            <div className="form-group">
              <textarea
                value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                rows="4"
                placeholder="Enter any additional comments or findings..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="button" className="preview-btn" disabled={isSubmitting} onClick={handlePreview}>
              {isSubmitting ? 'Generating...' : 'Preview Report'}
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Generating Report...' : 'Generate Report'}
            </button>
          </div>

          {/* Message Display */}
          {message.text && (
  <div className={`message ${message.type}`}>
    {message.text}
  </div>
)}

        </form>
      </div>
    </div>
  );
}

export default BPPForm;
